/**
 * Opportunity card on the Sales > Opportunities (stage) and Sales >
 * Pipeline (current-step) kanban boards.
 *
 * Mirrors `LeadCard` but renders the MEDDIC-extended shape per Claude
 * Design Files README section 5.7: mono company line, bold opportunity
 * name, large tabular value, stage / step / probability chips. Won
 * receives a 3px green left border; lost dims and gets a red border.
 */

import {
  OPPORTUNITY_ACTION_COLORS,
  OPPORTUNITY_ACTION_LABELS,
  type OpportunityAction,
} from '@workspace/shared';

import type { WireOpp } from '@/lib/api/sales';
import { cn } from '@/lib/utils';

interface OpportunityCardProps {
  /** The opportunity record to render. */
  opportunity: WireOpp;
  /** Callback fired when the card is clicked. */
  onClick: (id: string) => void;
  /** Which axis is the card grouped on — drives the right-side chip. */
  axis: 'stage' | 'currentAction';
}

export function OpportunityCard({ opportunity, onClick, axis }: OpportunityCardProps) {
  const value =
    opportunity.budgetFee && parseFloat(opportunity.budgetFee) > 0
      ? formatCurrency(opportunity.budgetFee)
      : null;

  const isWon = opportunity.stage === 'WON';
  const isLost = opportunity.stage === 'LOST';

  // Right-side chip label depends on which axis the board groups on.
  // The Pipeline board groups by Current Action, so the chip surfaces the
  // win probability for at-a-glance triage; the Opportunities board groups
  // by stage, so the chip surfaces the Current Action.
  const rightChip =
    axis === 'currentAction'
      ? opportunity.winProbability
      : (OPPORTUNITY_ACTION_LABELS[opportunity.currentAction as OpportunityAction] ??
        opportunity.currentAction);

  return (
    <article
      data-testid="opportunity-card"
      data-opportunity-id={opportunity.id}
      onClick={() => onClick(opportunity.id)}
      className={cn(
        'group relative cursor-pointer select-none border border-mx-grey-800 bg-mx-card p-mx-3 transition-colors duration-mx-default ease-mx-default hover:border-mx-grey-700',
        isWon && 'border-l-[3px] border-l-mx-pos',
        isLost && 'border-l-[3px] border-l-mx-neg opacity-65',
      )}
    >
      <p className="mono-label text-mx-grey-mid">{opportunity.companyName}</p>
      <h3 className="mt-mx-1 text-mx-body-sm font-medium text-mx-grey-300">
        {opportunity.opportunityName}
      </h3>
      <div className="mt-mx-2 flex items-center justify-between">
        {value ? (
          <span className="tnum font-display text-mx-body-sm font-semibold text-mx-grey-300">
            {value}
          </span>
        ) : (
          <span className="mono-label text-mx-grey-mid">NO BUDGET</span>
        )}
        {axis === 'currentAction' ? (
          <span className="mono-label border border-mx-grey-800 bg-mx-grey-900 px-mx-2 py-[2px] text-mx-mono-sm text-mx-grey-mid">
            {rightChip.replace(/_/g, ' ')}
          </span>
        ) : (
          <span
            className="mono-label truncate px-mx-2 py-[2px] text-mx-mono-sm"
            style={{
              backgroundColor:
                OPPORTUNITY_ACTION_COLORS[opportunity.currentAction as OpportunityAction]?.bg,
              color: OPPORTUNITY_ACTION_COLORS[opportunity.currentAction as OpportunityAction]?.fg,
            }}
          >
            {rightChip}
          </span>
        )}
      </div>
    </article>
  );
}

/**
 * Format a decimal-string fee as a compact AUD value with the MAXART
 * tabular-numeral pattern: `$3.84M`, `$640K`, `$1,200`.
 */
function formatCurrency(value: string): string {
  const num = parseFloat(value);
  if (Number.isNaN(num)) return value;
  if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(2)}M`;
  if (num >= 1_000) return `$${(num / 1_000).toFixed(0)}K`;
  return `$${num.toFixed(0)}`;
}
